/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import gsap from "gsap";
import "gsap/EasePack";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollSmoother from "gsap/ScrollSmoother";
import DrawSVGPlugin from "gsap/DrawSVGPlugin";
gsap.registerPlugin(ScrollSmoother, ScrollTrigger, SplitText, DrawSVGPlugin);

import Swiper from "swiper";
import {
	Autoplay,
	FreeMode,
	Navigation,
	Pagination,
	Parallax,
	Scrollbar,
} from "swiper/modules";

import { Grid, html } from "gridjs";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

// Default : Smooth scrolling
let scrollSmooth = false;

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'page-no-js' body class
	document.body.classList.remove("page-no-js");
	gsapPreloader();
	scrollDetection(160);
	menuBurger();
	menuDropdown();
	bannerInit();
	bannerToMarquee();
	dataSrc();
	dataClone();
	dataPopin();
	dataPassword();
	detailsToggle();
	textareaAutoResize();
	formLoader();
	gridInit();
});

// DOCUMENT RESIZED (avec ScrollTrigger)
ScrollTrigger.addEventListener("refreshInit", () => {
	gsapScrollSmootherRefresh();
	bannerToMarquee();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Activation/désactivation du scroll
function scrollEnabled(enable = true) {
	if (scrollSmooth) {
		scrollSmooth.paused(!enable);
	}
	document.documentElement.style.overflowY = enable ? "auto" : "hidden";
}

// Scroll vers une ancre
const scrollToAnchor = (scrollOffset = 0) => {
	// Fonction pour gérer le défilement
	const scrollToElement = (target) => {
		const element = document.querySelector(target);
		if (element) {
			if (scrollSmooth) {
				// Avec ScrollSmoother
				scrollSmooth.scrollTo(target, true, `top ${scrollOffset}px`);
			} else {
				// Par défaut
				window.scrollTo({
					top:
						element.getBoundingClientRect().top + window.scrollY - scrollOffset,
					behavior: "smooth",
				});
			}
		}
	};

	// au chargement
	const hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollToElement(hashInUrl);
	}

	// avec un [href="#..."]
	const linksWithAnchor = document.querySelectorAll('a[href^="#"]');
	linksWithAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchorHref = link.getAttribute("href");
			if (anchorHref !== "#") {
				event.preventDefault();
				scrollToElement(anchorHref);
			}
		});
	});

	// avec un [data-scrollto]
	const dataWithScrollTo = document.querySelectorAll("[data-scrollto]");
	dataWithScrollTo.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.dataset.scrollto;
			if (anchorData === "auto") {
				// Calculer la position de défilement pour 'auto'
				const autoOffset = window.innerHeight + window.scrollY - scrollOffset;
				window.scrollTo({
					top: autoOffset,
					behavior: "smooth",
				});
			} else {
				// Utiliser la valeur de data-scrollto comme cible pour scrollToElement
				scrollToElement(anchorData);
			}
		});
	});
};

// Détection du scroll sur le body (Optimisé avec ScrollTrigger)
const scrollDetection = (scrollOffset = 1) => {
	// État initial
	if (window.scrollY > scrollOffset) {
		document.body.classList.add("page-scrolled");
	}

	ScrollTrigger.create({
		start: 0,
		end: "max",
		onUpdate: (self) => {
			const scroll = self.scroll();

			// Gestion de page-scrolled
			if (scroll > scrollOffset) {
				document.body.classList.add("page-scrolled");
			} else {
				document.body.classList.remove("page-scrolled");
			}

			// Gestion de page-scrolling (direction : 1 = bas, -1 = haut)
			if (self.direction === 1 && scroll > scrollOffset) {
				document.body.classList.add("page-scrolling");
			} else {
				document.body.classList.remove("page-scrolling");
			}

			// Gestion de page-scroll-completed
			if (self.progress === 1) {
				document.body.classList.add("page-scroll-completed");
			} else {
				document.body.classList.remove("page-scroll-completed");
			}
		},
	});
};

// Gestion du menu burger
const menuBurger = () => {
	const headerBurger = document.querySelector("#page-header-burger");
	const headerBackdrop = document.querySelector("#page-header-backdrop");

	if (headerBurger) {
		headerBurger.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullmenu");
				gsapMenuBurger();
			}
		});
	}

	if (headerBackdrop) {
		headerBackdrop.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullmenu");
			}
		});
	}
};

// Gestion des dropdowns
const menuDropdown = () => {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]',
	);

	menuItemLinks.forEach((item) => {
		item.closest(".menu__item").classList.add("menu__item--parent");
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active",
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
};

// Activation/désactivation de la bannière
const bannerInit = () => {
	// Vérifie si la bannière a déjà été fermée durant cette session
	if (sessionStorage.getItem("bannerClosed") === "true") {
		return;
	}

	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// actualisation
		document.body.classList.add("page-with-banner");
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");

			// Enregistre en session que la bannière a été fermée
			sessionStorage.setItem("bannerClosed", "true");
		});
	}
};

// Gestion du défilement horizontal de la bannière
const bannerToMarquee = () => {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("page__banner--scrolling");
		} else {
			pageBanner.classList.add("page__banner--scrolling");
		}
	}
};

// Convertir les [data-src] en [src]
const dataSrc = () => {
	// Select all elements with a data-src attribute
	const srcElements = document.querySelectorAll("[data-src]");
	srcElements.forEach((element) => {
		element.setAttribute("src", element.dataset.src);
		element.removeAttribute("data-src");
	});
};

// Cloner les éléménts [data-clone]
const dataClone = () => {
	const cloneTargets = document.querySelectorAll("[data-clone]");

	cloneTargets.forEach((cloneOriginal) => {
		const cloneValue = cloneOriginal.dataset.clone;
		const cloneParent = cloneOriginal.parentNode;

		if (!cloneParent) return;
		let cloneCount;

		if (cloneValue === "auto" || !cloneValue || cloneValue === undefined) {
			const cloneWidth = cloneOriginal.offsetWidth;
			cloneCount = Math.ceil(window.innerWidth / cloneWidth) + 1;
			// console.log("cloneCount: " + cloneCount);
		} else {
			const parsedCount = parseInt(cloneValue, 10);
			if (isNaN(parsedCount) || parsedCount <= 0) return;
			cloneCount = parsedCount;
		}

		for (let i = 0; i < cloneCount; i++) {
			const clone = cloneOriginal.cloneNode(true);
			clone.removeAttribute("data-clone");
			clone.setAttribute("data-clone-child", `${i + 1}/${cloneCount}`);
			cloneParent.insertBefore(clone, cloneOriginal);
		}

		cloneOriginal.remove(); // Supprimer l'élément initial après clonage
	});
};

// Gestions des actions des [data-popin]
const dataPopin = () => {
	const popins = document.querySelectorAll("[data-popin]");

	popins.forEach((popin) => {
		popin.addEventListener("click", (event) => {
			// event.preventDefault();

			// ouvrir/fermer la popin
			const thispopinName = popin.dataset.popin;
			if (thispopinName) {
				const thispopinEl = document.querySelector(
					`.page__popin#${thispopinName}`,
				);
				thispopinEl.classList.toggle("page__popin--open");
			}

			// mettre les vidéos des popins en pause
			const htmlVideoFrompopin =
				document.querySelectorAll(".page__popin video");
			if (htmlVideoFrompopin.length > 0) {
				htmlVideoFrompopin.forEach((video) => {
					video.pause();
				});
			}
		});
	});
};

// Gestion des boutons [data-password]
const dataPassword = () => {
	const passwordButtons = document.querySelectorAll("[data-password]");

	passwordButtons.forEach((button) => {
		button.addEventListener("click", (event) => {
			event.preventDefault();
			const input = document.querySelector(`#${button.dataset.password}`);
			input.type = input.type === "password" ? "text" : "password";
			button.classList.toggle("--active");
		});
	});
};

// Gestion de l'accordéon (details/summary)
const detailsToggle = () => {
	const details = document.querySelectorAll("details");

	details.forEach((el) => {
		const summary = el.querySelector("summary");

		summary.addEventListener("click", (e) => {
			e.preventDefault();

			// Anti-rebond
			if (el.dataset.animating) return;
			el.dataset.animating = "true";

			if (el.open) {
				// FERMETURE
				const startHeight = el.offsetHeight;
				const endHeight = summary.offsetHeight; // Taille du résumé = taille fermé

				el.style.height = `${startHeight}px`;
				el.style.overflow = "hidden";

				requestAnimationFrame(() => {
					el.style.height = `${endHeight}px`;
				});

				setTimeout(() => {
					el.removeAttribute("open");
					el.style.height = "";
					el.style.overflow = "";
					delete el.dataset.animating;
				}, 300); // 300ms = durée transition CSS
			} else {
				// OUVERTURE
				const startHeight = el.offsetHeight;

				el.style.height = `${startHeight}px`;
				el.style.overflow = "hidden";
				el.setAttribute("open", "");

				const endHeight = el.scrollHeight + (el.offsetHeight - el.clientHeight);

				requestAnimationFrame(() => {
					el.style.height = `${endHeight}px`;
				});

				setTimeout(() => {
					el.style.height = "";
					el.style.overflow = "";
					delete el.dataset.animating;
				}, 300);
			}
		});
	});
};

// Adapter la taille des textarea au débordement
const textareaAutoResize = () => {
	const textareas = document.querySelectorAll("textarea.form__control");
	const offset = 8;

	textareas.forEach((textarea) => {
		// Définir la hauteur initiale
		textarea.style.height = "auto";
		textarea.style.height = textarea.scrollHeight + offset + "px";

		// Écouter les changements de contenu
		textarea.addEventListener("input", () => {
			textarea.style.height = "auto";
			textarea.style.height = textarea.scrollHeight + offset + "px";
		});

		// Écouter les changements de taille de la fenêtre
		window.addEventListener("resize", () => {
			textarea.style.height = "auto";
			textarea.style.height = textarea.scrollHeight + offset + "px";
		});

		// Écouter les ouvertures de dialog
		const dialog = document.querySelectorAll('dialog');
		dialog.forEach(dialog => {
			dialog.addEventListener('toggle', (event) => {
				if (event.newState === 'open') {
					textarea.style.height = "auto";
					textarea.style.height = textarea.scrollHeight + offset + "px";
				}
			});
		});
	});
}

// Gestion des formulaires
const formLoader = () => {
	document.querySelectorAll("form").forEach((form) => {
		form.addEventListener("submit", (e) => {
			const submitBtn = e.currentTarget.querySelector('[type="submit"]');
			if (!submitBtn) return;

			// Désactiver le bouton (éviter le double submit)
			submitBtn.disabled = true;

			// Masquer l'icône par défaut et afficher le loader
			const iconNormal = submitBtn.querySelector("i:not(.--hidden)");
			const iconLoader = submitBtn.querySelector("i.--hidden");

			iconNormal?.classList.add("--hidden");
			iconLoader?.classList.remove("--hidden");
		});
	});
};

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
const swiperInit = () => {
	const swiperElements = document.querySelectorAll(".swiper[data-swiper]");
	swiperElements.forEach((swiper) => {
		const slides = swiper.querySelectorAll(".swiper-wrapper .swiper-slide");
		if (slides.length > 0) {
			const swiperOptions = swiper.dataset.swiper.trim().split(/\s+/);

			// Vérifier si le viewport est assez large pour 'no-mobile'
			const isNoMobile = swiperOptions.includes("no-mobile");
			const isViewportLarge = window.innerWidth > 768;

			if (isNoMobile && !isViewportLarge) return;

			const config = {
				slidesPerView: "auto",
				speed: 600,
				modules: [
					Autoplay,
					FreeMode,
					Navigation,
					Pagination,
					Parallax,
					Scrollbar,
				],
				navigation: {
					prevEl: swiper.querySelector(".swiper-navigation-prev"),
					nextEl: swiper.querySelector(".swiper-navigation-next"),
					disabledClass: "--disabled",
					lockClass: "--hidden",
				},
				pagination: {
					el: swiper.querySelector(".swiper-pagination"),
					clickable: true,
					hiddenClass: "--hidden",
				},
				scrollbar: {
					el: swiper.querySelector(".swiper-scrollbar"),
					draggable: true,
					hide: true,
				},
				parallax: true,
			};

			if (swiperOptions.includes("autoplay"))
				config.autoplay = { delay: 4500, disableOnInteraction: false };
			if (swiperOptions.includes("loop")) config.loop = true;
			if (swiperOptions.includes("free")) config.freeMode = true;
			if (swiperOptions.includes("centered")) config.centeredSlides = true;

			const swiperInstance = new Swiper(swiper, config);
		}
	});
};

/* --------------------------------- (GRID) --------------------------------- */

// Grid : Initialisation des tableaux
const gridInit = () => {
	const dataElement = document.querySelector("#grid-data");
	const tableElement = document.querySelector("#grid-table");

	if (dataElement && tableElement) {
		const columns = [];
		dataElement.querySelectorAll("thead th").forEach((th) => {
			columns.push({
				name: th.innerHTML,
				sort: {
					compare: (a, b) => {
						const valA = a.dataset;
						const valB = b.dataset;

						if (valA === valB) return 0;

						const numA = parseFloat(valA);
						const numB = parseFloat(valB);
						if (
							!isNaN(numA) &&
							!isNaN(numB) &&
							numA.toString() === valA &&
							numB.toString() === valB
						) {
							return numA > numB ? 1 : -1;
						}

						return valA > valB ? 1 : -1;
					},
				},
				formatter: (cell) => html(cell.html),
			});
		});

		const data = [];
		dataElement.querySelectorAll("tbody tr").forEach((tr) => {
			const ObjectRow = [];
			tr.querySelectorAll("td").forEach((td) => {
				const htmlContent = td.innerHTML;
				const textContent = td.innerText.trim();
				const dataContent = td.getAttribute("data-content") || textContent;

				ObjectRow.push({
					html: htmlContent,
					text: textContent,
					dataset: dataContent,
					toString: function () {
						return this.text;
					},
				});
			});
			data.push(ObjectRow);
		});

		dataElement.style.display = "none";

		const initialSearch = tableElement.dataset.search || '';

		new Grid({
			columns: columns,
			data: data,
			sort: true,
			search: {
				selector: (cell) => `${cell.dataset} ${cell.text}`,
			},
			language: {
				search: { placeholder: "Rechercher..." },
				pagination: {
					previous: "Précédent",
					next: "Suivant",
					showing: "Affichage de",
					results: () => "résultats",
				},
				noRecordsFound: "Aucun dossier correspondant trouvé",
				loading: "Chargement...",
			},
		}).render(tableElement);

		if (initialSearch) {
			const observer = new MutationObserver(() => {
				const searchInput = tableElement.querySelector('input[type="search"]');
				if (searchInput) {
					observer.disconnect();
					searchInput.value = initialSearch;
					searchInput.dispatchEvent(new Event('input', { bubbles: true }));
				}
			});
			observer.observe(tableElement, { childList: true, subtree: true });
		}
	}
};

/* --------------------------------- (GSAP) --------------------------------- */

// GSAP : Initialisation du scrollSmoother (avec ScrollSmoother)
const initScrollSmooth = () => {
	scrollSmooth = ScrollSmoother.create({
		wrapper: "#page-wrapper",
		content: "#page-container",
		smooth: 1,
		smoothTouch: false,
		effects: true,
		normalizeScroll: false,
		ignoreMobileResize: true,
	});
};

// GSAP : Rafraîchissement du ScrollSmoother (avec ScrollSmoother)
const gsapScrollSmootherRefresh = () => {
	if (scrollSmooth) {
		// Désactiver sur Élémentor + formulaire de candidature
		if (document.querySelector("body.elementor-editor-active") || document.querySelector("body.page-single-folder")) {
			scrollSmooth.kill();
		} else {
			scrollSmooth.refresh();
		}
	}
};

// GSAP : Animations liés au menuBurger
const gsapMenuBurger = () => {
	const burgerTimeline = gsap.timeline();
	burgerTimeline.from(
		[
			"#page-header .header__fullmenu .menu__item",
			"#page-header .header__fullmenu__actions",
		],
		{
			duration: 0.6,
			stagger: 0.05,
			ease: "power1.out",
			autoAlpha: 0,
			x: 40,
		},
	);
};

// GSAP : Animations au scroll (avec ScrollTrigger)
const gsapDataAnimation = () => {
	const SCROLL_TRIGGER_START = "top 99%";
	const ANIMATION_OFFSET_Y = 40;
	const DELAY_MULTIPLIER = 10;

	// Utilitaires internes
	const getDelay = (el) =>
		parseFloat(el.dataset.animationDelay) / DELAY_MULTIPLIER || 0.1;

	const createTrigger = (el, opts = {}) => ({
		trigger: el,
		start: SCROLL_TRIGGER_START,
		toggleActions: "play none none reverse",
		onEnter: () => el.classList.add("--animated"),
		onLeaveBack: () => el.classList.remove("--animated"),
		...opts,
	});

	// Stratégies d'animation
	const strategies = {
		// Apparition simple (Défaut)
		reveal: (el) => {
			gsap.from(el, {
				scrollTrigger: createTrigger(el),
				y: ANIMATION_OFFSET_Y,
				autoAlpha: 0,
				duration: 0.6,
				ease: "power2.out",
				delay: getDelay(el),
			});
		},
		// Enfants en cascade
		children: (el) => {
			gsap.from(el.children, {
				scrollTrigger: createTrigger(el),
				y: ANIMATION_OFFSET_Y,
				autoAlpha: 0,
				duration: 1.2,
				ease: "power4.out",
				delay: getDelay(el),
				stagger: 0.1,
			});
		},
		// Texte ligne par ligne / mots
		text: (el) => {
			SplitText.create(el, {
				type: "lines, words",
				autoSplit: true,
				// mask: "lines",
				onSplit(split) {
					gsap.from(split.words, {
						scrollTrigger: createTrigger(el),
						// y: "50%",
						x: 20,
						autoAlpha: 0,
						delay: getDelay(el),
						duration: 1.2,
						ease: "power3.out",
						stagger: 0.04,
					});
				},
			});
		},
		// Texte ligne par ligne / mots + svg
		title: (el) => {
			SplitText.create(el, {
				type: "lines, words",
				autoSplit: true,
				// mask: "lines",
				onSplit(split) {
					// Après le split, ajouter l'icône de dessin dans le dernier <em>
					const lastEm = el.querySelector("em:last-of-type");
					if (lastEm) {
						lastEm.insertAdjacentHTML(
							"beforeend",
							`<i data-animation="draw" data-animation-delay="6">
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 234 17">
									<path fill="none" stroke="var(--color-primary)" stroke-width="12" stroke-linecap="round" d="M5.9 5.9c52.7 6.7 184 6.1 222 0" />
								</svg>
							</i>`,
						);
						// Initialisation de l'animation 'draw' sur l'élément inséré
						const newAnim = lastEm.querySelector('[data-animation="draw"]');
						if (newAnim) strategies.draw(newAnim);
					}
					gsap.from(split.words, {
						scrollTrigger: createTrigger(el),
						// y: "100%",
						x: 20,
						autoAlpha: 0,
						delay: getDelay(el),
						duration: 1.2,
						ease: "power3.out",
						stagger: 0.04,
					});
				},
			});
		},
		// Lecture progressive
		reading: (el) => {
			SplitText.create(el, {
				type: "words, chars",
				onSplit(split) {
					// Créer une timeline pour séquencer texte puis images
					const timeline = gsap.timeline({
						scrollTrigger: createTrigger(el, {
							end: "bottom 50%",
							scrub: true,
						}),
					});

					// Animation du texte
					timeline.fromTo(
						split.words,
						{ opacity: 0.2 },
						{
							opacity: 1,
							stagger: 0.4,
							ease: "power1.out",
						},
						0,
					);

					// Animation des images
					const images = el.querySelectorAll("img");
					if (images.length > 0) {
						timeline.from(
							images,
							{
								opacity: 0,
								y: ANIMATION_OFFSET_Y,
								duration: 1.2,
								ease: "power1.out",
							},
							">",
						);
					}
				},
			});
		},
		// Dessiner les SVG enfants
		draw: (el) => {
			const svgs = el.querySelectorAll("svg");
			if (svgs.length > 0) {
				svgs.forEach((svg) => {
					const paths = svg.querySelectorAll(
						"path, circle, rect, line, polyline, polygon",
					);
					if (paths.length > 0) {
						gsap.from(paths, {
							scrollTrigger: createTrigger(el),
							drawSVG: "0%",
							duration: 0.6,
							ease: "power2.out",
							delay: getDelay(el),
							stagger: 0.1,
						});
					}
				});
			}
		},
	};

	// Gestion des éléments avec [data-animation]
	const animatedElements = document.querySelectorAll(
		"#page-container [data-animation]",
	);
	animatedElements.forEach((el) => {
		// Ignorer si l'élément est dans une zone [data-animation="none"]
		if (el.closest('[data-animation="none"]')) return;

		const type = el.dataset.animation;
		if (strategies[type]) {
			strategies[type](el);
		} else {
			// Si attribut vide ou inconnu -> reveal
			strategies.reveal(el);
		}
	});

	// Recalculate ScrollTrigger to ensure pins (especially with ScrollSmoother)
	ScrollTrigger.refresh();

	// Gestion des élements via Elementor
	document
		.querySelectorAll(
			"#page-container .elementor-element:not([data-animation]) > .elementor-element",
		)
		.forEach((el) => {
			if (el.closest('[data-animation="none"]')) return;
			strategies.reveal(el);
		});
	document
		.querySelectorAll("#page-container .elementor-heading-title")
		.forEach((el) => {
			if (el.closest("[data-animation]")) return;
			strategies.title(el);
		});
};

// Actions exécutées après le preLoader d'accueil
const afterPreloader = () => {
	scrollEnabled(true);
	swiperInit();
	gsapDataAnimation();
	scrollToAnchor();
};

// GSAP : Gestion du preloader d'accueil
const gsapPreloader = () => {
	// Chargement des fonctions GSAP préalables
	initScrollSmooth();
	scrollEnabled(false);

	// Lancement de l'animation de chargement
	const loaderTimeline = gsap.timeline();

	if (document.body.classList.contains("page-home")) {
		loaderTimeline
			// animation
			.to("#page-preloader .preloader__logo", {
				duration: 0.6,
				ease: "power1.out",
				opacity: 1,
			})
			.to("#page-preloader .preloader__wrapper", {
				duration: 0.6,
				ease: "power2.out",
				clipPath: "inset(0 0 100% 0)",
			});
	} else {
		loaderTimeline
			// animation
			.to("#page-preloader .preloader__wrapper", {
				delay: 0.15,
				duration: 0.3,
				ease: "power1.out",
				opacity: 0,
			});
	}
	loaderTimeline.set("#page-preloader", {
		visibility: "hidden",
	});

	// Functions succédentes
	loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.1);
};

/* ---------------------------- (IFRAME : GiveWP) --------------------------- */

document.addEventListener("DOMContentLoaded", () => {
	const iframeGiveV2 = document.querySelector("[name='give-embed-form']");
	const iframeGiveV4 = document.querySelector("#give-form-shortcode-1");

	if (iframeGiveV2) {
		const injectStyles = () => {
			try {
				const doc =
					iframeGiveV2.contentDocument || iframeGiveV2.contentWindow?.document;
				if (!doc) return;

				// On crée le style
				const styleId = "custom-iframe-styles";
				if (!doc.getElementById(styleId)) {
					const style = doc.createElement("style");
					style.id = styleId;
					style.textContent = `
          .give-form {
            color: #1c2c91 !important;
            font-family: "Poppins", sans-serif !important;
						font-weight: 600;
          }
          .give-form .give-amount-heading,
					.give-form .give-personal-info-section fieldset:not(.ffm-field-container > fieldset) legend:first-of-type,
					.give-form .give-payment-details-section legend:first-of-type {
            color: #192a6b;
						font-size: 2.4rem;
            font-weight: 600;
          }
					.give-form #give-gateway-radio-list > li > .give-gateway + .give-gateway-option {
					  color: #1c2c91 !important;
            font-family: "Poppins", sans-serif !important;
						font-weight: 600;
					}
					.give-form #give-purchase-button {
            border-radius: 2em;
          }
					.give-form .give-donation-amount .give-text-input {
						font-size: 2rem;
					}
					.give-form .give-personal-info-section .give-donation-form-summary-section {
						display: none;
					}
        `;
					doc.head.appendChild(style);
				}

				// On lance aussi tes événements de calcul ici
				setupIframeEvents(doc);
			} catch (e) {
				console.error("Accès à l'iframe refusé (Cross-Origin Policy).", e);
			}
		};

		// On surveille le chargement
		iframeGiveV2.addEventListener("load", () => {
			// Petit délai pour laisser le JS interne de GiveWP construire le formulaire
			setTimeout(injectStyles, 500);
		});

		// Sécurité si déjà chargé
		if (iframeGiveV2.contentDocument?.readyState === "complete") {
			setTimeout(injectStyles, 500);
		}
	}

	if (iframeGiveV4) {
		const updateTaxReduction = (doc, montantBrut) => {
			const pElement = doc.querySelector(
				".givewp-elements-paragraph-paragraph-1 p",
			);
			if (!pElement) return;

			// Nettoyage et conversion en nombre
			const montant = parseFloat(
				montantBrut.replace(",", ".").replace(/[^0-9.]/g, ""),
			);

			if (!isNaN(montant) && montant > 0) {
				// Calculs
				const reduction = (montant * 0.66).toFixed(2).replace(".", ",");
				const coutReel = (montant * 0.34).toFixed(2).replace(".", ",");

				// Message
				pElement.innerHTML = `Votre don ne vous coûte réellement que <strong>${coutReel}&nbsp;€</strong> après réduction fiscale : réduction de <strong>${reduction}&nbsp;€</strong> (dans la limite de 20% du revenu imposable).`;
			}
		};

		const setupIframeEvents = () => {
			const doc =
				iframeGiveV4.contentDocument || iframeGiveV4.contentWindow.document;
			if (!doc) return;

			// Délégation de clic pour les boutons de montant
			doc.addEventListener("click", (e) => {
				const btn = e.target.closest(".givewp-fields-amount__level");
				if (btn) {
					updateTaxReduction(doc, btn.textContent);
				}
			});

			// Écouteur sur l'input personnalisé
			const customInput = doc.querySelector("#amount-custom");
			if (customInput) {
				customInput.addEventListener("input", (e) => {
					updateTaxReduction(doc, e.target.value);
				});
			}

			// Initialisation au chargement (pour le montant sélectionné par défaut)
			const selectedBtn = doc.querySelector(
				".givewp-fields-amount__level--selected",
			);
			if (selectedBtn) {
				updateTaxReduction(doc, selectedBtn.textContent);
			}
		};

		// Gestion du chargement
		iframeGiveV4.onload = setupIframeEvents;

		// Sécurité au cas où l'iframe est déjà chargée (cache)
		if (
			iframeGiveV4.contentDocument &&
			iframeGiveV4.contentDocument.readyState === "complete"
		) {
			setupIframeEvents();
		}
	}
});
